import { useEffect, useRef, useState } from 'react'
import { useTexte } from '../../texte'
import { useEditMessage, useToggleReaction } from '../../hooks/queries'
import type { Message } from '../../api/types'
import { zerlege } from '../../lib/auszeichnung'
import { dauer as tonDauer } from '../../lib/sprachaufnahme'
import { Highlightkarten } from './Highlightkarten'
import { Abstimmungskarte } from './Abstimmungskarte'
import { bubbleTime, medienwort } from '../../lib/format'
import {
  CheckIcon,
  DoubleCheckIcon,
  GruppeIcon,
  KopierenIcon,
  PencilIcon,
  PersonIcon,
  PinIcon,
  ReplyIcon,
  TrashIcon,
} from '../../components/Icons'
import { Langtext } from '../../components/Langtext'
import { Lightbox } from './Gallery'
import { Restzeichen } from '../../components/Restzeichen'
import { useTextgrenzen } from '../../hooks/useTextgrenzen'
import { Blasenmenue, useLangdruck, type Menueeintrag, type Menueort } from './Blasenmenue'
import { useOnline } from '../../hooks/useOutbox'
import { Gallery } from './Gallery'
import { VideoPlayer } from './VideoPlayer'
import { EMOJI, Reactions } from './Reactions'

interface Props {
  message: Message
  tripId: number
  withUser?: number
  /** Namen über die Sprechblase setzen (im Antwortverlauf des Absenders). */
  showAuthor: boolean
  /** In einer abgeschlossenen Reise wird nichts mehr geschrieben. */
  canReact: boolean
  /**
   * Die Uhr der Reise — für die Kärtchen einer Freigabe.
   *
   * Sie gilt für jeden Punkt ohne eigene; ohne sie stünde auf dem Kärtchen die
   * Zeit des Geräts, und der Bericht läse sich zu Hause um Stunden verschoben.
   */
  reiseVersatz?: number | null
  /** Beschriftung für eine private Nachricht -- je nachdem, wer sie liest. */
  directLabel?: string
  /**
   * Trägt auch ein Beitrag an alle seine Marke?
   *
   * **Eine Einstellung der Reise** (`trips.mark_broadcast`), keine des
   * Kontos: es ist eine Aussage über diesen Bericht. Die private Nachricht
   * trägt ihre unabhängig davon — sie ist die Ausnahme, und die gehört
   * benannt.
   */
  markiereBroadcast?: boolean
  /**
   * „Darauf antworten" -- ohne diese Funktion gibt es den Knopf nicht.
   *
   * `mediaId` kommt aus der Großansicht eines Fotos: dann bezieht sich die
   * Antwort auf genau dieses Bild und nicht auf den ganzen Beitrag.
   */
  onReply?: (message: Message, mediaId?: number) => void
  /** Zum bezogenen Beitrag springen -- der Tipp aufs Zitat. */
  onJump?: (messageId: number) => void
  /**
   * „Auf die Karte" — aus diesem Beitrag ein Highlight machen.
   *
   * Nur der Reisende bekommt das, und nur an eigenen Beiträgen an alle. Ohne diese
   * Funktion gibt es den Knopf nicht.
   */
  onAufDieKarte?: (message: Message) => void
  /**
   * Der Auswahlmodus zum Loeschen (#30) -- oder nichts.
   *
   * **Der Verlauf haelt ihn, nicht die Blase.** Eine Auswahl ueber mehrere
   * Nachrichten kann keine einzelne kennen; hier kommt nur an, was diese eine
   * angeht.
   *
   * **Und er kommt nur an eigenen, nicht geloeschten Nachrichten an.** Der
   * Kreis ist eine Bequemlichkeit; die Sperre steht im Server
   * (`author_id = :user`) und bleibt dort. Zwei Stellen fuer dieselbe Regel
   * sind in diesem Projekt schon mehrfach auseinandergelaufen -- deshalb ist
   * die hier ausdruecklich keine.
   */
  auswahl?: { gewaehlt: boolean; waehle: () => void }
  /** „Löschen" im Menü: den Auswahlmodus öffnen, diese Nachricht schon darin. */
  onLoeschmodus?: (messageId: number) => void
}

/** Eine Sprechblase. Eigene Nachrichten rechts, fremde links. */
export function Bubble({
  message,
  tripId,
  withUser,
  showAuthor,
  canReact,
  reiseVersatz = null,
  directLabel,
  markiereBroadcast = false,
  onReply,
  onJump,
  onAufDieKarte,
  auswahl,
  onLoeschmodus,
}: Props) {
  const grenzen = useTextgrenzen()
  /** Das zitierte Foto in Großansicht -- nur wenn der Server eines mitschickt. */
  const [zitatOffen, setZitatOffen] = useState(false)
  const zitatBild = message.reply_to?.media_full ?? null
  const edit = useEditMessage(tripId, withUser)
  const react = useToggleReaction(tripId, withUser)

  // Reaktionen auf die Nachricht selbst; die an den Fotos zeigt die Galerie.
  const own = message.reactions.filter((reaction) => reaction.media_id === 0)
  const bilder = message.media.filter((item) => item.kind === 'image')
  const t = useTexte()
  const [draft, setDraft] = useState<string | null>(null)
  /** Wo das Menü aufgehen soll — `null` heißt: es ist zu. */
  const [menue, setMenue] = useState<Menueort | null>(null)
  const field = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (draft !== null) field.current?.focus()
  }, [draft])

  // Wo überhaupt geantwortet werden kann: an einer fremden, nicht gelöschten
  // Sprechblase in einer Reise, die noch läuft -- und nicht, während gerade
  // etwas geändert wird.
  const antwortbar =
    onReply !== undefined && canReact && !message.own && !message.deleted && draft === null

  function save() {
    const text = (draft ?? '').trim()
    // Unverändert oder leer: nichts senden. Eine leere Nachricht lehnt der
    // Server ohnehin ab, und Löschen ist ein eigener Knopf.
    if (text === '' || text === message.body) {
      setDraft(null)
      return
    }
    edit.mutate({ id: message.id, body: text }, { onSuccess: () => setDraft(null) })
  }

  /**
   * **Gibt es überhaupt etwas zu lesen?**
   *
   * Aus dem Betrieb: ein Foto ohne Text saß in einer Sprechblase, und die war
   * nichts als ein farbiger Rahmen um ein Bild. Wo nichts steht, steht jetzt
   * auch keine Blase — das Bild spricht für sich.
   *
   * Der Zitatbalken zählt **nicht** dazu: er gehört zu dem, worauf geantwortet
   * wird, und steht dann eben über dem Anhang.
   */
  /**
   * **Kärtchen sprechen für sich — wie ein Foto.**
   *
   * Über ihnen stand einmal ein Satz („Neu auf der Karte"), und darum herum
   * eine Sprechblase. Beides wiederholte, was auf dem Kärtchen ohnehin steht:
   * Titel, Ort, Datum. Aus dem Betrieb gewünscht, und es ist dieselbe
   * Entscheidung wie oben beim Foto — wo nichts zu lesen steht, steht auch
   * keine Blase.
   *
   * Der **gespeicherte** Text bleibt, wie er ist: er trägt die Vorschau in
   * der Chatliste und die Meldung auf dem Sperrbildschirm, und dort gibt es
   * keine Kärtchen.
   */
  const hatKarten = !message.deleted && (message.highlights?.items.length ?? 0) > 0

  /**
   * **Dieselbe Überlegung wie bei den Kärtchen** (#56): der gespeicherte Text
   * („Abstimmung: Wohin morgen?") trägt die Vorschau in der Reiseliste und die
   * Meldung auf dem Sperrbildschirm — dort gibt es keine Abstimmung zum
   * Antippen. In der Sprechblase stünde die Frage damit zweimal.
   */
  const hatAbstimmung = !message.deleted && message.poll !== null

  const hatText =
    message.deleted || draft !== null || (message.body !== '' && !hatKarten && !hatAbstimmung)

  /** Wer es geschrieben hat, und ob es privat war — vor allem anderen. */
  const kopf = (
    <>
      {showAuthor && !message.deleted && (
        <span className="bubble__author">{message.author.display_name}</span>
      )}

      {/* Worauf sich das hier bezieht. Steht **über** allem anderen, denn
          es ist der Anfang des Satzes: erst das Zitat, dann die Antwort
          darauf. */}
      {message.reply_to && !message.deleted && (
        /*
         * **Zwei Ziele statt einem** — aus dem Betrieb: „bei der Antwort auf
         * ein Foto soll der Empfänger das Bild auch direkt in der Galerie
         * öffnen können (das thumbnail ist manchmal zu klein um zu erkennen,
         * welches Foto es ist)."
         *
         * Das Bildchen öffnet die Großansicht, der Rest springt weiterhin zur
         * bezogenen Nachricht. Beides in **einen** Knopf zu legen ginge nicht:
         * ein `<button>` in einem `<button>` ist kein gültiges Dokument, und
         * die Sprungausgabe der Tastatur käme durcheinander. Also ein Kasten
         * mit zwei Knöpfen darin.
         */
        <div className="zitat">
          {zitatBild !== null ? (
            <button
              type="button"
              className="zitat__lupe"
              onClick={() => setZitatOffen(true)}
              aria-label={t.chat.fotoGross}
              title={t.chat.fotoGrossKurz}
            >
              <img className="zitat__bild" src={message.reply_to.media_url ?? zitatBild.url} alt="" />
            </button>
          ) : (
            message.reply_to.media_url && (
              <img className="zitat__bild" src={message.reply_to.media_url} alt="" />
            )
          )}
          <button
            type="button"
            className="zitat__sprung"
            onClick={() => onJump?.(message.reply_to!.id)}
            title={t.chat.zumBezug}
          >
            <span className="zitat__text">
              <span className="zitat__autor">{message.reply_to.author}</span>
              <span className="zitat__auszug">
                {message.reply_to.deleted
                  ? t.chat.geloescht
                  : message.reply_to.body !== ''
                    ? message.reply_to.body
                    : medienwort(message.reply_to.media_kind)}
              </span>
            </span>
          </button>
        </div>
      )}

      {/* **Dieselbe Großansicht wie überall** -- mit Zoom, Sichern und allem.
          Nur zu reagieren gibt es hier nichts: das Foto gehört einer anderen
          Nachricht, und wer darauf antworten will, springt hin. */}
      {zitatOffen && zitatBild !== null && (
        <Lightbox
          media={[zitatBild]}
          start={0}
          reactionsFor={() => []}
          canReact={false}
          onReact={() => undefined}
          onClose={() => setZitatOffen(false)}
        />
      )}

      {/* Eine Nachricht an einen Einzelnen muss als solche dastehen. Ohne
          diese Zeile hält der Mitleser sie für einen Travel Broadcast -- und
          antwortet, als hätten alle mitgelesen.

          **Eine Person statt eines Schlosses.** Aus dem Betrieb: „das deutet
          aber auf Verschlüsselung hin." Es deutet auf eine, die es nicht
          gibt -- und mit #23 gäbe es sie und das Zeichen meinte trotzdem
          etwas anderes. Eine Person sagt, was gemeint ist: **einer** sieht
          es.

          **Nur das Sinnbild, kein Wort.** Aus dem Betrieb: „ohne Wort, nur
          Symbol" -- so, wie das Schloss vorher dastand. Ein Wort an jeder
          Blase ist eine Zeile, die bei jeder Nachricht dasselbe sagt.

          **Der Satz verschwindet trotzdem nicht**, er wird unsichtbar: wer
          vorlesen lässt, hört weiterhin, wie weit die Nachricht reicht. Er
          steht als Text im Dokument und nicht als `aria-label` an einem
          Element ohne Rolle; ein `title` allein ist auf dem Telefon ohnehin
          unsichtbar. Das Sinnbild daneben trägt `aria-hidden`, sonst käme
          sein Name vor den Satz. */}
      {message.kind === 'direct' && !message.deleted && (
        <span className="bubble__reichweite" title={directLabel ?? t.chat.nurAnDich}>
          <PersonIcon size={15} aria-hidden="true" />
          <span className="visually-hidden">{directLabel ?? t.chat.nurAnDich}</span>
        </span>
      )}

      {/* **Und was an alle ging, sagt das auch** -- wenn die Reise es so
          hält. Aus dem Betrieb: „beide markieren, aber die Option auch im
          Broadcast konfigurierbar machen für den Admin - auf Reise-Ebene".
          Ein Bericht, in dem ohnehin fast alles an alle geht, trüge sonst an
          jeder Blase dieselbe Marke; einer mit vielen Einzelnachrichten
          gewinnt durch sie. Deshalb je Reise und nicht ein für alle Mal. */}
      {markiereBroadcast && message.kind === 'broadcast' && !message.deleted && (
        <span className="bubble__reichweite" title={t.chat.anAlleEmpfaenger}>
          <GruppeIcon size={15} aria-hidden="true" />
          <span className="visually-hidden">{t.chat.anAlle}</span>
        </span>
      )}
    </>
  )

  /**
   * Zeit, Haken und die Knöpfe.
   *
   * Sie hängen an der **Nachricht** und nicht an der Blase. Deshalb stehen sie
   * hier einmal und werden dorthin gesetzt, wo die Nachricht aufhört: in die
   * Blase, wenn es eine gibt — sonst unter den Anhang.
   */
  const meta = (
    <span className={hatText ? 'bubble__meta' : 'bubble__meta bubble__meta--frei'}>
      {bubbleTime(message.created_at)}
      {message.edited_at && !message.deleted && (
        <span title={t.chat.nachtraeglichGeaendert}>{t.chat.bearbeitet}</span>
      )}
      <Haken read={message.read} />
    </span>
  )

  /**
   * Was man mit dieser Nachricht tun kann — im Menü statt in der Blase (#8).
   *
   * **Die Bedingungen sind dieselben wie vorher**, nur der Ort ist ein
   * anderer: hier stand vier Mal ein `<button>` mit genau diesen `&&`
   * davor. Zeit, „bearbeitet" und Haken sind absichtlich **nicht**
   * mitgegangen — sie sagen etwas über die Nachricht und tun nichts;
   * ausdrücklich so gewünscht („hier würde ich beim WhatsApp-Stil bleiben").
   */
  /**
   * Das eine Medium, wenn es genau eines ist.
   *
   * **Damit „Antworten" im Menü so genau bleibt wie der Knopf, der dafür
   * weggefallen ist.** Am Video stand „Auf dieses Video antworten" und gab
   * dessen Kennung mit; ohne sie zeigte das Zitat über der Eingabezeile kein
   * Standbild und darunter das Wort „Foto". Aus dem Betrieb: „außerdem kann
   * dann bei Videos der Knopf ‚auf dieses Video antworten' entfallen."
   *
   * **Abgeleitet und nicht am Griff festgemacht:** wo genau der Finger lag,
   * müsste jedes Medium einzeln melden. Bei genau einem Anhang ist die Frage
   * ohnehin entschieden -- und ein Video liegt praktisch immer allein in
   * seiner Nachricht. Bei mehreren Fotos führt der Weg weiter über die
   * Großansicht, die die Kennung kennt.
   */
  const einzelnesMedium = message.media.length === 1 ? message.media[0] : undefined

  const eintraege: Menueeintrag[] = []
  if (!message.deleted && draft === null) {
    // Auf eine eigene Nachricht zu antworten ergibt keinen Sinn.
    if (antwortbar) {
      eintraege.push({
        name: t.chat.menueAntworten,
        symbol: <ReplyIcon size={17} />,
        tun: () => onReply(message, einzelnesMedium?.id),
      })
    }
    if (hatText) {
      eintraege.push({
        name: t.chat.menueKopieren,
        symbol: <KopierenIcon size={17} />,
        // `writeText` kann fehlschlagen -- ohne sicheren Kontext, ohne
        // Erlaubnis. Dann passiert nichts, und das ist hier in Ordnung: es
        // ist kein Schreibzugriff auf die Reise, sondern eine Bequemlichkeit
        // des Geräts.
        tun: () => void navigator.clipboard?.writeText(message.body).catch(() => undefined),
      })
    }
    if (message.own) {
      // Aus dem Absatz einen Punkt auf der Karte machen. Text und Zeitpunkt
      // gehen mit hinüber; wo es war, entscheidet die Karte.
      if (onAufDieKarte) {
        eintraege.push({
          name: t.chat.menueAufDieKarte,
          symbol: <PinIcon size={17} />,
          tun: () => onAufDieKarte(message),
        })
      }
      // **Diesen Text schreibt die Karte, nicht der Mensch.** Er steht in
      // der Chatliste und auf dem Sperrbildschirm, in der Sprechblase gar
      // nicht mehr — ein selbst geschriebener Satz wäre hier also
      // unsichtbar. Deshalb kein Ändern; der Server lehnt es zusätzlich ab.
      if (!hatKarten) {
        eintraege.push({
          name: t.chat.menueAendern,
          symbol: <PencilIcon size={17} />,
          tun: () => setDraft(message.body),
        })
      }
      /*
       * **Löschen öffnet die Auswahl, statt sofort zu fragen** (#30).
       *
       * Aus dem Betrieb, nach dem Vorbild von WhatsApp und iMessage: „nach
       * Auswahl von Löschen im Menü öffnet sich unten eine Leiste mit einem
       * Papierkorb-Symbol; die Nachricht, aus der heraus das Löschen
       * aufgerufen wurde, ist bereits markiert."
       *
       * Für eine einzelne Nachricht ist das ein Griff mehr als vorher. Der
       * Gewinn steht daneben: zehn Nachrichten kosten jetzt auch nur einen.
       */
      eintraege.push({
        name: t.chat.menueLoeschen,
        symbol: <TrashIcon size={17} />,
        gefahr: true,
        tun: () => onLoeschmodus?.(message.id),
      })
    }
  }

  const griffe = useLangdruck(setMenue)

  /**
   * Die Emoji-Reihe über dem Menü — dieselbe Liste wie im Server.
   *
   * **Ohne Netz abgeschaltet, nicht stumm.** Ein Emoji ist ein Schreibzugriff
   * wie jeder andere; das war schon am alten Knopf so und bleibt es hier.
   */
  const online = useOnline()
  const reaktionen = canReact && !message.deleted
    ? {
        emojis: EMOJI,
        eigene: own.filter((reaktion) => reaktion.mine).map((reaktion) => reaktion.emoji),
        gesperrt: !online || react.isPending,
        hinweis: online ? undefined : t.chat.ohneNetz,
        waehle: (emoji: string) => react.mutate({ messageId: message.id, emoji }),
      }
    : undefined

  /**
   * Fotos und Videos — **unter** der Blase, nicht darin.
   *
   * Aus dem Betrieb gewünscht, und es ist auch das treffendere Bild: eine
   * Sprechblase ist für Gesagtes. Ein Bild ist kein Satz, es braucht keinen
   * Rahmen, und in der Blase drängte es den Text an den Rand.
   *
   * Videos stehen für sich, Bilder in der Galerie. Ein Video in ein Raster aus
   * Quadraten zu stecken hieße, einen Player auf ein Vorschaubild zu
   * schrumpfen -- und die Großansicht der Galerie ist fürs Blättern durch
   * Fotos gebaut, nicht fürs Abspielen.
   */
  const anhang = message.deleted ? null : (
    <>
      {/* **Sprachnachrichten zuerst** — sie sind die Nachricht, nicht ihr
          Anhang. Ein eingebauter Player und kein eigener: das Bedienelement
          des Geräts kennt jeder, und ein nachgebauter kann weniger. */}
      {message.media
        .filter((item) => item.kind === 'audio')
        .map((item) => (
          <div className="tonblase" key={item.id}>
            <audio controls preload="metadata" src={item.url} className="tonblase__player">
              Dein Browser spielt diese Aufnahme nicht ab.
            </audio>
            {item.duration_ms > 0 && (
              <span className="tonblase__dauer">{tonDauer(item.duration_ms)}</span>
            )}
          </div>
        ))}

      {message.media
        .filter((item) => item.kind === 'video')
        .map((item) => (
          <VideoPlayer key={item.id} media={item} />
        ))}

      {bilder.length > 0 && (
        <Gallery
          media={bilder}
          reactions={message.reactions}
          canReact={canReact}
          onReact={(emoji, mediaId) =>
            react.mutate({ messageId: message.id, emoji, mediaId })
          }
          onReply={antwortbar ? (mediaId) => onReply!(message, mediaId) : undefined}
        />
      )}
    </>
  )
  const hatAnhang = !message.deleted && message.media.length > 0

  const reaktionsliste = !message.deleted && own.length > 0 && (
    <>
      <Reactions
        reactions={own}
        busy={react.isPending}
        // Der Knopf zum Reagieren liegt jetzt im Menü beim langen Drücken --
        // hier steht nur noch, was vergeben ist.
        nurAnzeigen
        onPick={(emoji) => canReact && react.mutate({ messageId: message.id, emoji })}
      />
      {/* Stillschweigend nichts tun gibt es nicht -- auch ein Emoji sagt,
          wenn es nicht ankam (Reise abgeschlossen, Nachricht geloescht). */}
      {react.isError && (
        <span className="feld__hinweis" role="alert">
          {react.error instanceof Error ? react.error.message : t.allgemein.schiefgelaufen}
        </span>
      )}
    </>
  )

  return (
    <div
      className={[
        'bubble-row',
        message.own ? 'bubble-row--own' : '',
        // Nur wo ein Kreis steht, wird auch Platz dafür gemacht.
        auswahl === undefined ? '' : 'bubble-row--waehlbar',
        auswahl?.gewaehlt === true ? 'bubble-row--gewaehlt' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      // Das Ziel für den Sprung aus einem Zitat.
      id={`msg-${message.id}`}
      /**
       * Der Griff zum Menü liegt an der ganzen Zeile und nicht an der Blase:
       * eine Nachricht aus einem einzigen Foto hat gar keine Blase.
       *
       * **Nur, wenn es etwas zu tun gibt.** Sonst wäre jede fremde Nachricht
       * in einer abgeschlossenen Reise eine Station in der Tab-Reihenfolge,
       * die auf ein leeres Menü führt.
       */
      {...(auswahl !== undefined
        ? { onClick: auswahl.waehle }
        : eintraege.length > 0
          ? griffe
          : {})}
      tabIndex={eintraege.length > 0 ? 0 : undefined}
      role={eintraege.length > 0 ? 'button' : undefined}
      aria-haspopup={eintraege.length > 0 ? 'menu' : undefined}
      aria-label={eintraege.length > 0 ? t.chat.blaseName : undefined}
    >
      {/*
        * **Der Kreis steht vor der Nachricht** (#30), wie in WhatsApp.
        *
        * Er ist ein echter Knopf und keine Verzierung: 44 × 44, mit
        * `aria-pressed`, damit eine Vorlesehilfe den Zustand nennt. Ein Tipp
        * auf die Blase selbst schaltet ihn ebenfalls um -- den Weg nimmt der
        * Daumen, der Kreis ist die Auskunft, dass es ihn gibt.
        */}
      {auswahl !== undefined && (
        <button
          type="button"
          className={auswahl.gewaehlt ? 'wahlkreis wahlkreis--an' : 'wahlkreis'}
          aria-pressed={auswahl.gewaehlt}
          aria-label={auswahl.gewaehlt ? t.chat.auswahlAufheben : t.chat.zumLoeschenWaehlen}
          onClick={(event) => {
            event.stopPropagation()
            auswahl.waehle()
          }}
        >
          <span className="wahlkreis__punkt" />
        </button>
      )}
      {menue !== null && (
        <Blasenmenue
          eintraege={eintraege}
          reaktionen={reaktionen}
          ort={menue}
          onSchliessen={() => setMenue(null)}
        />
      )}
      {hatText && (
        <div className={message.deleted ? 'bubble bubble--deleted' : 'bubble'}>
          {kopf}
          {meta}

          {draft !== null ? (
            <div className="stack" style={{ gap: 6 }}>
              <textarea
                ref={field}
                className="bubble__edit"
                rows={Math.min(8, draft.split('\n').length + 1)}
                value={draft}
                /* Anders als das Schreibfeld unten ist dieses Feld gesteuert
                   -- hier sperrt `maxLength` also, statt nur zu zählen. */
                maxLength={grenzen.message}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  // Enter sendet, Umschalt+Enter macht einen Absatz -- wie im
                  // Schreibfeld darunter.
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    save()
                  }
                  if (event.key === 'Escape') setDraft(null)
                }}
              />
              <Restzeichen wert={draft} grenze={grenzen.message} />
              <div className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="button"
                  style={{ padding: '6px 12px', fontSize: 14 }}
                  disabled={edit.isPending}
                  onClick={save}
                >
                  {edit.isPending ? t.allgemein.laeuft : t.allgemein.speichern}
                </button>
                <button
                  type="button"
                  className="button button--outline"
                  style={{ padding: '6px 12px', fontSize: 14 }}
                  onClick={() => setDraft(null)}
                >
                  {t.allgemein.abbrechen}
                </button>
              </div>
              {edit.isError && (
                <span style={{ color: 'var(--danger)', fontSize: 13 }}>
                  {edit.error instanceof Error ? edit.error.message : t.allgemein.schiefgelaufen}
                </span>
              )}
            </div>
          ) : message.deleted ? (
            t.chat.geloescht
          ) : (
            /**
             * **Freigaben stehen hier nicht mehr.**
             *
             * Der gespeicherte Wortlaut („Neu auf der Karte: Nidarosdom")
             * trägt weiterhin die Vorschau in der Reiseliste und die Meldung
             * auf dem Sperrbildschirm — dort gibt es keine Kärtchen. In der
             * Sprechblase stünde der Titel damit zweimal, einmal im Satz und
             * einmal auf dem Kärtchen; deshalb kommt eine Nachricht mit
             * Kärtchen gar nicht bis hierher (siehe `hatText`).
             *
             * **Beiträge von vor der Kärtchen-Änderung tragen keine** und
             * zeigen unverändert ihren Text — sonst stünde dort nichts.
             */
            /* **Der Deckel sitzt aussen, die Auszeichnung innen.** Der
               Deckel misst am rohen Wortlaut und zeichnet nichts selbst --
               sonst gaebe es zwei Wege, einen Text darzustellen. */
            <Langtext text={message.body}>
              <Ausgezeichnet text={message.body} />
            </Langtext>
          )}

          {!hatAnhang && !hatKarten && reaktionsliste}
        </div>
      )}

      {/* **Die Kärtchen stehen für sich, ohne Blase** — derselbe Bau wie beim
          Anhang darunter, und aus demselben Grund. Kopf und Meta wandern
          hierher, wenn es keine Blase gibt: sonst wüsste niemand, von wem der
          Beitrag ist und wann er kam. */}
      {hatKarten && (
        <div className="hlblock">
          {!hatText && kopf}
          <Highlightkarten message={message} reiseVersatz={reiseVersatz} />
          {!hatText && meta}
          {!hatAnhang && reaktionsliste}
        </div>
      )}

      {/* **Die Abstimmung steht für sich, ohne Blase** — derselbe Bau wie die
          Kärtchen darüber und aus demselben Grund: sie ist breiter als eine
          Sprechblase und braucht Trefferflächen. */}
      {hatAbstimmung && (
        <div className="hlblock">
          {!hatText && kopf}
          <Abstimmungskarte poll={message.poll!} tripId={tripId} />
          {!hatText && meta}
          {!hatAnhang && reaktionsliste}
        </div>
      )}

      {hatAnhang && (
        <div className="anhang">
          {/* Ohne Blase steht der Kopf hier -- sonst wüsste bei einem Foto
              ohne Text niemand, worauf es sich bezieht. */}
          {!hatText && kopf}
          {anhang}
          {!hatText && meta}
          {reaktionsliste}
        </div>
      )}
    </div>
  )
}

/**
 * Der Haken an einer eigenen Nachricht.
 *
 * **Ein Haken** heißt „liegt bereit", **zwei** heißen „gelesen" — dieselbe
 * Bedeutung wie in der Chatliste, und aus demselben Grund keine dritte Stufe:
 * ein „zugestellt" im Sinne eines Messengers kann es hier nicht geben. Ohne
 * dauerhafte Verbindung weiß der Server nicht, ob ein Gerät die Nachricht
 * schon geholt hat.
 *
 * Geht die Nachricht an mehrere — ein Rundbeitrag an alle Empfänger —, steht
 * die Zahl daneben: *2/5*. Erst wenn alle gelesen haben, wird sie grün. Ohne
 * die Zahl wäre der Haken eine Behauptung über eine Gruppe, in der er für die
 * meisten nicht stimmt.
 *
 * Ist `read` null, steht hier nichts. Wo nichts zugestellt wird, ist jeder
 * Haken eine Behauptung ins Blaue.
 */
function Haken({ read }: { read: { by: number; of: number } | null }) {
  const t = useTexte()
  if (read === null) return null

  const alle = read.by >= read.of
  const text =
    read.of === 1
      ? alle
        ? t.chat.gelesen
        : t.chat.liegtBereit
      : t.chat.vonGelesen(read.by, read.of)

  return (
    <span
      className={alle ? 'bubble__haken bubble__haken--gelesen' : 'bubble__haken'}
      title={text}
    >
      {read.by > 0 ? <DoubleCheckIcon size={13} /> : <CheckIcon size={13} />}
      {read.of > 1 && (
        <span className="bubble__haken-zahl">
          {read.by}/{read.of}
        </span>
      )}
      <span className="visually-hidden">{text}</span>
    </span>
  )
}

/**
 * Der Text einer Nachricht — mit Auszeichnung, aber ohne HTML.
 *
 * **Zerlegt und als Text gesetzt, nie über `dangerouslySetInnerHTML`.** Der
 * Text kommt von Menschen und geht an Menschen; ein Chat, in dem jemand
 * Markup unterbringen kann, ist keiner. Die Regel selbst steht in
 * `lib/auszeichnung.ts` — hier steht nur, welches Stück welche Klasse
 * bekommt.
 */
function Ausgezeichnet({ text }: { text: string }) {
  return (
    <>
      {zerlege(text).map((stueck, i) =>
        stueck.art === 'text' ? (
          <span key={i}>{stueck.text}</span>
        ) : (
          <span key={i} className={`aus aus--${stueck.art}`}>
            {stueck.text}
          </span>
        ),
      )}
    </>
  )
}
